import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin, Ticket, Minus, Plus } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Spinner, EmptyState } from "../components/states";
import { PayDialog } from "../components/PayDialog";
import { toast } from "sonner";
import { BackButton, Breadcrumbs, ExternalLink, useEscape } from "../lib/nav";

export default function Events() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [qty, setQty] = useState({});
  const [order, setOrder] = useState(null);
  useEffect(() => { api.get("/events").then((r) => { setEvents(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);
  if (loading) return <Spinner />;

  const total = sel ? sel.ticket_types.reduce((s, t) => s + (qty[t.id] || 0) * t.price, 0) : 0;
  const checkout = async () => {
    if (!user) { toast.error("Sign in to buy tickets"); nav("/login"); return; }
    const items = Object.entries(qty).filter(([, q]) => q > 0).map(([ticket_type_id, q]) => ({ ticket_type_id, qty: q }));
    if (!items.length) return;
    try { const { data } = await api.post("/orders", { event_id: sel.id, items }); setOrder(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not reserve tickets"); }
  };

  return (
    <div className="mx-auto max-w-[1600px] px-4 sm:px-6 py-8">
      <div className="flex items-center gap-4 flex-wrap mb-4"><BackButton fallback="/" /><Breadcrumbs items={[{ label: "Live", to: "/" }, { label: "Events" }]} /></div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight">Events</h1>
      <p className="mono-label text-[#71717A] mt-2">UPCOMING WAREHOUSE NIGHTS · SECURE QR TICKETS</p>
      {events.length === 0 ? <EmptyState title="No events scheduled" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {events.map((e, i) => {
            const from = Math.min(...e.ticket_types.map((t) => t.price));
            const left = e.ticket_types.reduce((s, t) => s + t.available, 0);
            return (
              <div key={e.id} data-testid={`event-${e.id}`} className="card-copper rounded-xl overflow-hidden fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
                <div className="aspect-video overflow-hidden bg-[#1A1A22] relative">{e.image && <img src={e.image} alt={e.title} className="w-full h-full object-cover" />}
                  {e.status !== "published" && <span className="absolute top-2 left-2 mono-label bg-[#0A0A0C]/80 border border-[#FF9F1C] text-[#FF9F1C] rounded px-2 py-0.5">{e.status.toUpperCase()}</span>}</div>
                <div className="p-5">
                  <p className="font-display text-xl font-bold">{e.title}</p>
                  <div className="flex items-center gap-2 mono-label text-[#E8552A] mt-2"><Calendar size={13} />{new Date(e.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}{e.doors && ` · DOORS ${e.doors}`}</div>
                  <div className="flex items-center gap-2 mono-label text-[#71717A] mt-1"><MapPin size={13} />{e.venue}</div>
                  <p className="text-sm text-[#A1A1AA] mt-3 line-clamp-2">{e.description}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="font-display font-bold text-[#E8552A]">{isFinite(from) ? `from ₹${from}` : "—"} <span className="mono-label text-[#71717A]">· {left} LEFT</span></span>
                    {e.status === "published" && left > 0 ? <button data-testid={`buy-${e.id}`} onClick={() => { setSel(e); setQty({}); }} className="copper-btn rounded-full px-4 py-1.5 mono-label flex items-center gap-1"><Ticket size={13} /> GET TICKETS</button>
                      : <span className="mono-label text-[#71717A]">{left === 0 ? "SOLD OUT" : "SALES PAUSED"}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {sel && !order && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setSel(null)}>
          <div data-testid="ticket-picker" onClick={(e) => e.stopPropagation()} className="bg-[#121216] border border-[#27272A] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-6 fade-up">
            <h3 className="font-display text-xl font-bold uppercase">{sel.title}</h3>
            <div className="space-y-3 mt-4">
              {sel.ticket_types.map((t) => (
                <div key={t.id} className="flex items-center justify-between border border-[#27272A] rounded-lg px-4 py-3">
                  <div><p className="font-display font-semibold">{t.name}</p><p className="mono-label text-[#71717A]">₹{t.price} · {t.available} LEFT</p></div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty({ ...qty, [t.id]: Math.max(0, (qty[t.id] || 0) - 1) })} className="border border-[#27272A] rounded p-1"><Minus size={14} /></button>
                    <span data-testid={`qty-${t.id}`} className="w-6 text-center font-display font-bold">{qty[t.id] || 0}</span>
                    <button data-testid={`plus-${t.id}`} disabled={(qty[t.id] || 0) >= Math.min(10, t.available)} onClick={() => setQty({ ...qty, [t.id]: (qty[t.id] || 0) + 1 })} className="border border-[#27272A] rounded p-1 disabled:opacity-40"><Plus size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
            <button data-testid="ticket-checkout" disabled={total <= 0} onClick={checkout} className="w-full copper-btn rounded-lg py-3 mono-label mt-5 disabled:opacity-50">RESERVE & PAY ₹{total}</button>
          </div>
        </div>
      )}
      {order && <PayDialog purpose="ticket_order" orderId={order.id} title={`${order.event_title} · ${order.items.reduce((s, l) => s + l.qty, 0)} ticket(s)`} amount={order.amount} onClose={() => { setOrder(null); setSel(null); }} onPaid={() => nav("/dashboard")} />}
    </div>
  );
}
